/**
 * src/api/placesApi.ts — address autocomplete + serviceability + waitlist.
 *
 * Talks to our own Harper resources (the Google Places key stays server-side):
 *   POST /PlacesAutocomplete → predictions
 *   POST /PlaceDetails       → resolved address + lat/lng + serviceability
 *   POST /WaitlistSignup     → out-of-area divert (anonymous endpoint)
 *
 * A session token (one UUID per autocomplete "session" + its final details
 * call) is generated client-side and threaded through both Places calls so
 * Google bills per-session rather than per-keystroke.
 */

import { post } from './harperClient';

export interface PlacePrediction {
  placeId: string;
  mainText: string;
  secondaryText: string;
  description: string;
}

export interface PlaceAddressComponents {
  street: string;
  apt: string;
  city: string;
  state: string;
  zip: string;
}

export interface PlaceResolved {
  formattedAddress: string;
  lat: number | null;
  lng: number | null;
  components: PlaceAddressComponents;
  /** True when the resolved coordinates fall inside a served Denver cluster. */
  serviceable: boolean;
  clusterId: string | null;
}

/** Fresh session token for a new autocomplete session. */
export function newPlacesSessionToken(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `s-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

export async function placesAutocomplete(input: string, sessionToken: string): Promise<PlacePrediction[]> {
  const res = await post<{ suggestions: PlacePrediction[] }>('/PlacesAutocomplete', { input, sessionToken });
  return res.suggestions ?? [];
}

export async function placeDetails(placeId: string, sessionToken: string): Promise<PlaceResolved> {
  return post<PlaceResolved>('/PlaceDetails', { placeId, sessionToken });
}

/** Add an out-of-area customer to the pre-launch waitlist (Harper system of record). */
export async function joinWaitlist(email: string, zip: string): Promise<{ uuid: string }> {
  return post<{ uuid: string }>('/WaitlistSignup', { email, zip, source: 'attic-app-onboarding' });
}
